import { BehaviorSubject } from "rxjs/BehaviorSubject"
import { IWorkspaceElementRender } from "../PA.UIM.Reporting/PA.UIM.Reporting.IF"
import { IProgressProvider } from "../PA.Core/PA.Core.IF.Basic"
import * as _ from "lodash"
import { WB_MetricDefinitionRow, WB_ReportMetricRow, WB_ReportRow } from "../PA.Core/PAdbSchemaRows"
import { IPolicy } from "../PA.Core/PA.Core.IF.Policy"
import { IBusinessObject } from "../PA.Core/PA.Core.IF.BO"

// Connection

export interface IConnection {
    ConnectionId: string,
    ConnectionName: string,
    ConnectionTypeId: string,
    ConnectionOptions: { [id: string]: any }
}

// Hierarchy

export interface IHierarchy {
    HierarchyId: string,
    LeafType?: string,
    HierarchyName: string,
    HierarchyDescription: string
    WBMasterDataSetId: number
    WBCompanyId?: number
    WBSalesOrgId?: number
}

export interface IHierarchyLevel {
    LevelId: string,
    LevelName: string
}

export interface IHierarchyNode {
    NodeId: string,
    NodeIdUI: string,
    NodeName: string,
    NodeShortName: string,
    NodeLevel?: string,
    NodeObject?: object,
    ParentId?: string,
    WBMasterDataSetId?: number
    // Filter_Object?: string
}

export interface IDimensionDataProvider {
    getHierarchies(): Promise<IHierarchy[]>

    getHierarchyLevels(HierarchyId: string): Promise<IHierarchyLevel[]>

    getHierarchyNodes(HierarchyId: string, level_count?: number): Promise<IHierarchyNode[]>
}

export function instanceOfIDimensionDataProvider(obj: any): boolean {
    if (_.isUndefined(obj)) {
        return false
    }

    var checkFunction = ["getHierarchies", "getHierarchyLevels", "getHierarchyNodes"]
    var returnValue = true
    _.forEach(checkFunction, (functionName: string) => {
        if (!_.isFunction(obj[functionName])) {
            returnValue = false
            return false
        }
    })
    return returnValue
}


/// Dimension
//
export interface IDimension {
    DimensionId: string,
    DimensionName: string,
    DimensionTechnicalName: string,
    DimensionRenderOptions: { [id: string]: any },
    SortOrder?: number
}

///  DimensionSelection
//
export interface IDimensionSelector {
    OnLoadComplete: Promise<IBusinessObject>;
    Dimension: IDimension
    DimensionDataProvider: IDimensionDataProvider
    ProgressProvider: IProgressProvider

    readonly AllHierarchies: IHierarchy[]
    readonly Hierarchies: IHierarchy[]
    readonly OnHierarchyChanged: BehaviorSubject<IDimensionSelector> // Called when the list of Hierarchies have changed

    readonly HierarchyLevels: IHierarchyLevel[]
    readonly OnHierarchyLevelChanged: BehaviorSubject<IDimensionSelector> // Called when the list of Hierarchy Level have changed

    readonly HierarchyNodes: IHierarchyNode[]
    readonly OnHierarchyNodesChanged: BehaviorSubject<IDimensionSelector>  // Called when the list of Hierarchy Nodes have changed

    HierarchySelected: IHierarchy
    HierarchyLevelSelected: IHierarchyLevel
    HierarchyNodesSelected: IHierarchyNode[]

    readonly OnHierarchySelectedChanged: BehaviorSubject<IDimensionSelector>
    readonly OnHierarchyLevelSelectedChanged: BehaviorSubject<IDimensionSelector>
    readonly OnHierarchyNodesSelectedChanged: BehaviorSubject<IDimensionSelector>

    readonly OnSelectionChanged: BehaviorSubject<IDimensionSelector>

    LockSelectionToParent: boolean
    Parent: IDimensionSelector
    WBCompanyId?: number
    WBSalesOrgId?: number

}


//  Dimension Selection Group
//
export interface IDimensionSelectorSet {
    DimensionSelectors: IDimensionSelector[]

    readonly bIDimensionSelectorSetLoaded: boolean
    readonly OnIDimensionSelectorSetLoadComplete: Promise<IDimensionSelectorSet>

}


// Workspace Item

export interface IWorkspaceElementMessage {
    [id: string]: any
}

export interface IWorkspaceElementPost {
    WorkspaceElement: IWorkspaceElement,
    Message: IWorkspaceElementMessage
}


export interface IWorkspaceElement {
    readonly ElementId: string | number;
    readonly ElementTypeId: string;
    readonly ElementName: string;
    readonly ElementRenderer: IWorkspaceElementRender
    readonly ElementRenderOptions: { [id: string]: any }

    // Setup Renderer
    readonly ElementRendererFactoryName: string

    setElementRenderer(aRenderer: IWorkspaceElementRender): void

    PostInboxMessage(aMessage: IWorkspaceElementMessage): void

    OnInboxMessage: BehaviorSubject<IWorkspaceElementPost>

    PostOutboxMessage(aMessage: IWorkspaceElementMessage): void

    OnOutboxMessage: BehaviorSubject<IWorkspaceElementPost>
}


export interface IWorkspaceElementSet {
    addWorkspaceElement(anElement: IWorkspaceElement): void

    removeWorkspaceElement(aWorkspaceElementId: string | number, TypeId: string): void

    containsWorkspaceElement(aWorkspaceElementId: string | number, TypeId: string): boolean

    readonly WorkspaceElements: IWorkspaceElement[]
    readonly OnWorkspaceElementsChanged: BehaviorSubject<IWorkspaceElementSet>

    PostMessage(aMessage: IWorkspaceElementMessage): void

    MessageBus: BehaviorSubject<IWorkspaceElementPost>
}


// Report

export interface IReportTile {
    ReportId: string,
    ReportName: string,
    ReportDescription: string,
    ReportTypeId: string,
    ReportRenderOptions: { [id: string]: any },
    ReportHelpURL?: string
}


export interface IWorkspaceReport {
    WorkspaceReportId: number,
    Report: IReportTile,
    Connection: IConnection,
    DimensionSelectorGroup: IDimensionSelectorSet

}

// P&L Reporting
//   Report Workspace

export interface IWorkspaceReportContext {

    WB_ReportRows: WB_ReportRow[],
    WB_Report_ID_Selected: number,
    OnWorkspaceContextLoaded: BehaviorSubject<IWorkspaceReportContext>,

    dsCustomer: IDimensionSelector,
    dsProduct: IDimensionSelector,
    dsTime: IDimensionSelector,
    Policy: IPolicy,
    Currency: string,


    aggLevelCustomer: string,
    aggLevelProduct: string,
    aggLevelTime: string,

    WB_Snapshot_ID_Selected: number,
    WB_Company_ID_Selected: number,

    Execute: () => Promise<void>,
    bExecuted: boolean,

    Results: any[],
    OnResultsChanged: BehaviorSubject<IWorkspaceReportContext>

}

// Rendering Placement

export enum DimensionPlacement {
    OnColumns = "OnColumns",
    OnRows = "OnRows",
    OnPageBy = "OnPageBy",
    None = "None"
}


export interface IReportingService {
    getToken(): Promise<string>
}